import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { TextField, Modal, Typography } from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { toast } from "react-toastify";
import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import BASE_URL from "../../utils/baseUrl";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "15px",
};

function getToken() {
  const auth = getAuth();
  return auth.currentUser
    ? auth.currentUser.getIdToken()
    : Promise.reject("No user logged in");
}

function EditToolbar({ handleOpen }) {
  return (
    <GridToolbarContainer>
      {/* <Button color="primary" startIcon={<AddIcon />} onClick={handleOpen}>
        Add User
      </Button> */}
    </GridToolbarContainer>
  );
}

export default function FullFeaturedCrudGrid() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      if (user) {
        getUserData();
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const getUserData = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${BASE_URL}/user/all`, {
        headers: { token },
      });
      setRows(
        response.data.map((data) => ({
          ...data,
          id: data.uid,
          creationTime: new Date(data.metadata.creationTime).toDateString(),
          lastSignin: new Date(data.metadata.lastSignInTime).toDateString(),
        }))
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (user = {}) => {
    setSelectedUser(user);
    setIsEdit(!!user.id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    setSelectedUser({});
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const token = await getToken();
    const inputs = event.target.elements;
    const userData = {};
    for(let i = 0; i < inputs.length; i++) {
      if(inputs[i].name) {
        userData[inputs[i].name] = inputs[i].value;
      }
    }
    console.log(userData);

    try {
      if (isEdit) {
        await axios.put(
          `${BASE_URL}/user/${selectedUser.id}`,
          userData,
          {
            headers: { token },
          }
        );
        toast.success("User updated successfully");
      } else {
        await axios.post(`${BASE_URL}/user/users`, userData, {
          headers: { token },
        });
        toast.success("User created successfully");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      handleClose();
      getUserData();
    }
  };

  const handleDelete = async (id) => {
    const token = await getToken();
    try {
      await axios.delete(`${BASE_URL}/user/${id}`, {
        headers: { token },
      });
      toast.success("User deleted successfully");
      getUserData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  // Columns as initially provided
  const columns = [
    // { field: "id", headerName: "ID", width: 400, editable: false },
    { field: "name", headerName: "Name", flex: true },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      editable: false,
      flex: true,
    },
    { field: "designation", headerName: "Designation" },
    { field: "selfHelpGroup", headerName: "Self Help Group", flex: true },
    { field: "language", headerName: "Language", flex: true },
    { field: "location", headerName: "Location", flex: true },
    {
      field: "isMaternity",
      headerName: "Is Maternity",
      flex: true,
      type: "boolean",
    },
    { field: "hwc", headerName: "HWC", flex: true },
    { field: "creationTime", headerName: "Created At", flex: true },
    { field: "lastSignin", headerName: "Last Logged In", flex: true },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 70,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpen(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.id)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          components={{
            Toolbar: EditToolbar,
          }}
          componentsProps={{
            toolbar: { handleOpen: () => handleOpen() },
          }}
        />
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style} component="form" onSubmit={handleFormSubmit} noValidate>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {isEdit ? "Edit User" : "Add User"}
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            id="phoneNumber"
            label="Phone Number"
            name="phoneNumber"
            autoComplete="phoneNumber"
            disabled={isEdit}
            defaultValue={selectedUser.phoneNumber || ""}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Name"
            name="name"
            autoComplete="name"
            defaultValue={selectedUser.name || ""}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="designation"
            label="Designation"
            name="designation"
            defaultValue={selectedUser.designation || ""}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}
